import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const AUTH_BG =
    "https://static.prod-images.emergentagent.com/jobs/1ab3e5cd-a620-45d1-bd08-7360f4789ce9/images/b48c5b5accfe2a8fbea415c336ee8cb8fcf9ebb399ef7faa616d676c39395998.png";

/**
 * Lands on /reset-password?token=… after the user follows the dev-mode
 * link from the backend logs (or, in production, the email). Submits to
 * /auth/reset-password and on success the response carries a fresh JWT —
 * we apply it through AuthContext.applyAuth so the user is signed in
 * straight away with no extra step.
 */
export default function ResetPasswordPage() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const { applyAuth } = useAuth();

    const [pwd, setPwd] = useState("");
    const [confirm, setConfirm] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [done, setDone] = useState(false);

    useEffect(() => {
        // Surface a missing-token state immediately so users following a
        // truncated link aren't confused by the form silently failing.
        if (!token) {
            setError("This reset link is missing its token. Request a new one from the sign-in page.");
        }
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        if (!token) return;
        if (pwd.length < 6) {
            setError("Password must be at least 6 characters.");
            return;
        }
        if (pwd !== confirm) {
            setError("Passwords don't match.");
            return;
        }
        setError("");
        setSubmitting(true);
        try {
            const { data } = await api.post("/auth/reset-password", {
                token,
                new_password: pwd,
            });
            applyAuth(data.token, data.user);
            setDone(true);
            // Brief success beat, then bounce to the project list. Avoids
            // a jarring instant-redirect while the user is still reading
            // the success state.
            setTimeout(() => navigate("/", { replace: true }), 1200);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            data-testid="reset-password-page"
            className="relative min-h-screen w-full flex flex-col"
            style={{
                backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.55), rgba(0,0,0,0.85)), url(${AUTH_BG})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
            }}
        >
            <div className="flex-1 flex items-center justify-center px-5 py-10">
                <div className="w-full max-w-md">
                    <div className="mb-8">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-400">
                            Film &amp; TV continuity
                        </div>
                        <h1 className="font-display text-5xl sm:text-6xl font-black text-white leading-none mt-3">
                            OnSetFlow
                        </h1>
                    </div>

                    <div className="rounded-xl border border-white/10 bg-card/80 backdrop-blur-md p-5">
                        <h2 className="font-display text-xl font-bold text-white">
                            Set a new password
                        </h2>
                        <p className="text-xs text-neutral-400 mt-1.5 leading-relaxed">
                            Choose a password you&rsquo;ll remember. You&rsquo;ll be signed in straight after.
                        </p>

                        {done ? (
                            <div
                                data-testid="reset-success"
                                className="mt-5 rounded-lg border border-emerald-400/20 bg-emerald-400/5 p-4 flex items-start gap-3"
                            >
                                <CheckCircle2
                                    size={20}
                                    className="text-emerald-300 shrink-0 mt-0.5"
                                />
                                <div>
                                    <p className="text-sm text-emerald-200 font-semibold">
                                        Password updated.
                                    </p>
                                    <p className="text-xs text-neutral-400 mt-1">
                                        Redirecting you to your projects&hellip;
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="space-y-4 mt-5">
                                <div className="space-y-2">
                                    <Label
                                        htmlFor="reset-pwd"
                                        className="text-xs uppercase tracking-[0.2em] text-neutral-500"
                                    >
                                        New password{" "}
                                        <span className="normal-case tracking-normal text-neutral-600">
                                            (min 6)
                                        </span>
                                    </Label>
                                    <Input
                                        id="reset-pwd"
                                        data-testid="reset-pwd-input"
                                        type="password"
                                        autoComplete="new-password"
                                        value={pwd}
                                        onChange={(e) => setPwd(e.target.value)}
                                        required
                                        minLength={6}
                                        disabled={!token || submitting}
                                        className="h-12 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label
                                        htmlFor="reset-confirm"
                                        className="text-xs uppercase tracking-[0.2em] text-neutral-500"
                                    >
                                        Confirm password
                                    </Label>
                                    <Input
                                        id="reset-confirm"
                                        data-testid="reset-confirm-input"
                                        type="password"
                                        autoComplete="new-password"
                                        value={confirm}
                                        onChange={(e) => setConfirm(e.target.value)}
                                        required
                                        minLength={6}
                                        disabled={!token || submitting}
                                        className="h-12 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                                    />
                                </div>
                                {error ? (
                                    <p
                                        data-testid="reset-error"
                                        className="text-sm text-red-400"
                                    >
                                        {error}
                                    </p>
                                ) : null}
                                <Button
                                    data-testid="reset-submit"
                                    type="submit"
                                    disabled={!token || submitting}
                                    className="w-full min-tap font-semibold tracking-wide"
                                >
                                    {submitting ? (
                                        <>
                                            <Loader2 size={14} className="mr-2 animate-spin" />
                                            Updating&hellip;
                                        </>
                                    ) : (
                                        "Update password"
                                    )}
                                </Button>
                                <Link
                                    to="/login"
                                    data-testid="reset-back-login"
                                    className="tap block w-full text-center text-xs text-neutral-400 hover:text-white py-2"
                                >
                                    Back to sign in
                                </Link>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
